import React, { useCallback, useState } from 'react'

import { QueryClientProvider } from '@tanstack/react-query'

import { bool } from 'prop-types'

import { AUTH_MODAL_EVENT, MODE } from 'Pods/LazyLoaded/AiSignInModal/constants'
import CommunityGalleryPanel from 'Pods/LazyLoaded/CommunityGalleryPanel/CommunityGalleryPanel'
import { DEFAULT_COMMUNITY_GALLERY_FILTERS } from 'Pods/LazyLoaded/CommunityGalleryPanel/constants'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { buildSharedRunPathname } from 'Pods/ThreeDStudio/Hooks/buildSharedRunPathname'

import queryClient from 'Utils/queryClient'

import NoMatchNotice from './Components/NoMatchNotice/NoMatchNotice'

// Landing-page host for the shared CommunityGalleryPanel. The studio page wires
// the panel to its Zustand store and an in-app remix-and-open card action; this
// static marketing page owns the filters with local state and its card action is
// a cross-page navigation into the studio (see handleCardClick). The panel sources
// its category chips from the gallery endpoint's response. Rendered in loadMore
// mode so the button-driven pagination can be exercised here.
//
// Unlike the studio, a too-narrow filter here never dead-ends in an empty state:
// `enableFallback` swaps in popular models and the NoMatchNotice explains the
// swap, so the marketing page always showcases content.
function CommunityGallery({ extractingCoreStyles = false }) {
  const [searchQuery, setSearchQuery] = useState(DEFAULT_COMMUNITY_GALLERY_FILTERS.searchQuery)
  const [category, setCategory] = useState(DEFAULT_COMMUNITY_GALLERY_FILTERS.category)
  // This page wraps no ThreeDStudioPageContext around the gallery, so the card
  // like toggle reads the viewer from window.pageConfig directly. It is only
  // needed on click, and the hover-revealed chip reserves no layout space, so
  // resolving it after /load-services.js costs nothing visually.
  const pageConfig = usePageConfig()

  const handleClearFilters = useCallback(() => {
    setSearchQuery(DEFAULT_COMMUNITY_GALLERY_FILTERS.searchQuery)
    setCategory(DEFAULT_COMMUNITY_GALLERY_FILTERS.category)
  }, [])

  // Opening a model from the landing-page gallery sends the user into the studio's
  // Remix Mode, matching the in-studio gallery. This is a separate page, so origin
  // cannot ride React Router state (lost on a full navigation); it travels as
  // `?fromGallery=true`, which the studio promotes back into location.state on
  // arrival (see useGalleryOriginSync) to drive the Regenerate CTA and the
  // "You are in Remix Mode" notice.
  const handleCardClick = useCallback((model) => {
    window.location.assign(`${buildSharedRunPathname(model)}?fromGallery=true`)
  }, [])

  // The header Log in / Sign up buttons live in a separate UnifiedNavigation
  // tree, so the page's own sign-in modal is opened over App.events - the same
  // bridge UpgradeHeader uses (see useAuthModalTrigger).
  const handleSignInRequired = useCallback(() => {
    if (typeof App === 'undefined' || !App.events) return

    App.events.publish(AUTH_MODAL_EVENT, { mode: MODE.SIGNUP })
  }, [])

  const noMatchNotice = <NoMatchNotice onClearFilters={handleClearFilters} />

  return (
    <section className="ai-sub-landing__community-gallery">
      <QueryClientProvider client={queryClient}>
        <CommunityGalleryPanel
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          category={category}
          onCategoryChange={setCategory}
          paginationMode="loadMore"
          onCardClick={handleCardClick}
          emptyState={noMatchNotice}
          noMatchNotice={noMatchNotice}
          enableFallback
          extractingCoreStyles={extractingCoreStyles}
          authLoaded={Boolean(pageConfig)}
          isUserLoggedIn={Boolean(pageConfig?.isCurrentUser)}
          onSignInRequired={handleSignInRequired}
        />
      </QueryClientProvider>
    </section>
  )
}

CommunityGallery.propTypes = {
  extractingCoreStyles: bool,
}

export default CommunityGallery
